import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { DocumentPreview } from "../utils/function";
import { showToast } from "./toast";

// In-page document viewer: shows an image or PDF in a popup instead of opening a new tab
// (private documents need the Bearer token, which a new tab can't send). Download saves the
// file directly.
interface DocumentPreviewModalProps {
  title: string;
  load: () => Promise<DocumentPreview>;
  onDownload: () => Promise<void>;
  onClose: () => void;
}

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({ title, load, onDownload, onClose }) => {
  const [preview, setPreview] = useState<DocumentPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let loaded: DocumentPreview | null = null;
    load()
      .then((p) => {
        loaded = p;
        if (cancelled) p.release();
        else setPreview(p);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : "Failed to load document"));
    return () => {
      cancelled = true;
      loaded?.release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await onDownload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to download document", false);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-black truncate">{title}</h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleDownload}
              disabled={downloading || !!error}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-black text-white text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
            >
              <Download size={14} />
              {downloading ? "Downloading..." : "Download"}
            </button>
            <button
              onClick={onClose}
              aria-label="Close"
              className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 bg-gray-50 flex items-center justify-center overflow-auto">
          {error ? (
            <p className="text-sm text-gray-500 px-6 text-center">{error}</p>
          ) : !preview ? (
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#00875A]" />
          ) : preview.isImage ? (
            <img src={preview.src} alt={title} className="max-w-full max-h-full object-contain" />
          ) : (
            <iframe src={preview.src} title={title} className="w-full h-full border-0" />
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
